import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ApiError, api } from "./api";

export type AuthUser = {
  email: string;
  name: string;
  picture?: string | null;
};

export type AuthConfig = {
  enabled: boolean;
  google_client_id: string;
  allowed_email_domain: string;
};

type AuthContextValue = {
  user: AuthUser | null;
  config: AuthConfig | null;
  isLoading: boolean;
  authError: string | null;
  login: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  clearAuthError: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const LOCAL_DEV_USER: AuthUser = {
  email: "local@mengotomars.com",
  name: "Local User",
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const clearAuthError = useCallback(() => setAuthError(null), []);

  const loadSession = useCallback(async () => {
    setIsLoading(true);
    try {
      const nextConfig = await api.authConfig();
      setConfig(nextConfig);

      if (!nextConfig.enabled) {
        setUser(LOCAL_DEV_USER);
        setAuthError(null);
        return;
      }

      try {
        const me = await api.me();
        setUser(me.user ?? null);
        setAuthError(null);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setUser(null);
          return;
        }
        throw err;
      }
    } catch (err) {
      setUser(null);
      setAuthError(err instanceof Error ? err.message : "Authentication check failed");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const login = useCallback(async (code: string) => {
    setAuthError(null);
    const { user: nextUser } = await api.googleLogin(code);
    setUser(nextUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (err) {
      if (!(err instanceof ApiError) || err.status >= 500) throw err;
    }
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, config, isLoading, authError, login, logout, clearAuthError }),
    [user, config, isLoading, authError, login, logout, clearAuthError],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, config, isLoading } = useAuth();
  const loc = useLocation();

  if (isLoading || !config) {
    return <div className="p-8 text-neutral-400">Checking session...</div>;
  }
  if (config.enabled && !user) {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }
  return <>{children}</>;
}
